import { api } from "jiffy-api";
import { track } from "jiffy-analytics";
import { POLLING_ENABLED_CATALOG } from "./common/constants";

// Transfers polling service
class TransfersPollingService {
  constructor() {
    this.pollingInstances = new Map();
    this.INITIAL_DELAY = 40000;
    this.POLLING_INTERVAL = 10000;
    this.MAX_POLLING_DURATION = 180000;
  }

  // eslint-disable-next-line class-methods-use-this
  isPollingEnabled(catalogNumber) {
    return POLLING_ENABLED_CATALOG.includes(catalogNumber);
  }

  startPolling(artworkId, onEnhancementComplete, options = {}) {
    artworkId = Number(artworkId);
    if (this.pollingInstances.has(artworkId)) return;
    const { catalogNumber = null, dtfChannelUuid = null, checkArtworkExists = null } = options;
    const instance = {
      isPolling: true,
      pollCount: 0,
      startTime: Date.now(),
      timeoutId: null,
      catalogNumber,
      dtfChannelUuid,
      checkArtworkExists
    };
    this.pollingInstances.set(artworkId, instance);
    instance.timeoutId = setTimeout(() => {
      if (instance.checkArtworkExists && !instance.checkArtworkExists(artworkId)) {
        this.stopPolling(artworkId);
        return;
      }
      track("fallback_polling_started", {
        artwork_id: String(artworkId),
        catalog_number: instance.catalogNumber,
        page: "transfers_pdp"
      });
      this.#pollArtwork(artworkId, onEnhancementComplete);
    }, this.INITIAL_DELAY);
  }

  async #pollArtwork(artworkId, onEnhancementComplete) {
    const instance = this.pollingInstances.get(artworkId);
    if (!instance || !instance.isPolling) return;

    if (instance.checkArtworkExists && !instance.checkArtworkExists(artworkId)) {
      track("fallback_polling_stopped_artwork_removed", {
        artwork_id: String(artworkId),
        catalog_number: instance.catalogNumber,
        page: "transfers_pdp",
        poll_count: instance.pollCount
      });
      this.stopPolling(artworkId);
      return;
    }
    instance.pollCount += 1;
    const shouldContinue = await this.#checkArtworkStatus(artworkId, onEnhancementComplete);
    if (!shouldContinue) return;
    if (this.#isPollingTimedOut(instance)) {
      this.#handlePollingTimeout(artworkId);
      return;
    }
    this.#scheduleNextPoll(artworkId, onEnhancementComplete);
  }

  async #checkArtworkStatus(artworkId, onEnhancementComplete) {
    const instance = this.pollingInstances.get(artworkId);
    if (!instance) return false;
    try {
      const response = await api.dtf.getArtworkHistory({
        artwork_ids: [artworkId],
        catalog_number: instance.catalogNumber,
        dtf_channel_uuid: instance.dtfChannelUuid
      });

      const artwork = response.artworks.find(a => Number(a.id) === artworkId);
      const enhancementState =
        artwork?.current_enhancement_state || artwork?.currentEnhancementState;
      if (enhancementState === "complete_processing") {
        this.#handleEnhancementComplete(artworkId, artwork, onEnhancementComplete);
        return false;
      }
    } catch (error) {
      window.Honeybadger?.notify("Transfers polling error:", {
        params: {
          error: error instanceof Error ? error.message : String(error),
          hook: "transfers_polling_service",
          step: "check_artwork_status",
          artwork_id: String(artworkId),
          status_code: error.response?.status
        }
      });

      if (error.response?.status >= 500) {
        track("fallback_polling_stopped_server_error", {
          artwork_id: String(artworkId),
          catalog_number: instance.catalogNumber,
          page: "transfers_pdp"
        });
        this.stopPolling(artworkId);
        return false;
      }
    }
    return true;
  }

  #handleEnhancementComplete(artworkId, artwork, onEnhancementComplete) {
    const instance = this.pollingInstances.get(artworkId);
    const catalogNumber = instance?.catalogNumber;

    try {
      track("fallback_polling_enhancement_completed", {
        artwork_id: String(artworkId),
        catalog_number: catalogNumber,
        page: "transfers_pdp"
      });
      if (artwork) {
        const enhancedArtwork = TransfersPollingService.#transformArtworkData(artwork);
        onEnhancementComplete(enhancedArtwork);
      }
      this.stopPolling(artworkId);
    } catch (error) {
      window.Honeybadger?.notify("Error in handleEnhancementComplete:", {
        params: {
          error: error instanceof Error ? error.message : String(error),
          hook: "transfers_polling_service",
          step: "handle_enhancement_complete",
          artwork_id: String(artworkId)
        }
      });
      this.stopPolling(artworkId);
    }
  }

  #isPollingTimedOut(instance) {
    return Date.now() - instance.startTime >= this.MAX_POLLING_DURATION;
  }

  #handlePollingTimeout(artworkId) {
    const instance = this.pollingInstances.get(artworkId);
    this.stopPolling(artworkId);
    track("fallback_polling_stopped", {
      artwork_id: String(artworkId),
      catalog_number: instance?.catalogNumber,
      page: "transfers_pdp"
    });
  }

  #scheduleNextPoll(artworkId, onEnhancementComplete) {
    const instance = this.pollingInstances.get(artworkId);
    if (instance) {
      instance.timeoutId = setTimeout(() => {
        this.#pollArtwork(artworkId, onEnhancementComplete);
      }, this.POLLING_INTERVAL);
    }
  }

  stopPolling(artworkId) {
    const instance = this.pollingInstances.get(artworkId);
    if (instance) {
      if (instance.timeoutId) clearTimeout(instance.timeoutId);
      this.pollingInstances.delete(artworkId);
    }
  }

  stopAllPolling() {
    this.pollingInstances.forEach((instance, artworkId) => {
      this.stopPolling(artworkId);
    });
  }

  isPolling(artworkId) {
    return this.pollingInstances.has(artworkId);
  }

  static #transformArtworkData(artwork) {
    const { ordered, original } = artwork;
    const imageUrl = ordered?.image_url || ordered?.imageUrl;
    return {
      id: artwork.id,
      noBgUrl: ordered?.no_bg_url || ordered?.noBgUrl,
      noBgNoTrimUrl: ordered?.no_bg_no_trim_url || ordered?.noBgNoTrimUrl,
      withBgUrl: ordered?.with_bg_url || ordered?.withBgUrl,
      imageUrl,
      url: imageUrl,
      originalImageUrl: original?.image_url || original?.imageUrl,
      canRemoveBg: ordered?.can_remove_bg ?? ordered?.canRemoveBg,
      removeBackground: ordered?.remove_background ?? ordered?.removeBackground,
      metadata: ordered?.metadata,
      enhancedMetadata: ordered?.enhanced_metadata || ordered?.enhancedMetadata,
      originalMetadata: ordered?.original_metadata || ordered?.originalMetadata,
      withBgMetadata: ordered?.with_bg_metadata || ordered?.withBgMetadata
    };
  }
}

export default new TransfersPollingService();
